import api from "@/lib/api";

const DISMISS_KEY = "wu_push_prompt_dismissed";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIos() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

export function needsHomeScreen() {
  return isIos() && !isStandalone();
}

export function permissionState() {
  return pushSupported() ? Notification.permission : "unsupported";
}

export function promptDismissed() {
  return localStorage.getItem(DISMISS_KEY) === "1";
}

export function dismissPrompt() {
  localStorage.setItem(DISMISS_KEY, "1");
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function getRegistration() {
  if (!pushSupported()) return null;
  return navigator.serviceWorker.ready;
}

export async function currentSubscription() {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** Ask permission (must be inside a click handler) and register the device. */
export async function enablePush() {
  if (!pushSupported()) throw new Error("This browser doesn't support push notifications.");
  if (needsHomeScreen()) {
    throw new Error("On iPhone, add this app to your Home Screen first, then enable alerts from there.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked. Turn them on for this site in your browser settings."
        : "Notifications were not enabled.",
    );
  }

  const { data: config } = await api.get("/push/config");
  if (!config.configured || !config.public_key) throw new Error("Push is not configured on the server yet.");

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.public_key),
    });
  }
  const { data } = await api.post("/push/subscribe", sub.toJSON());
  dismissPrompt();
  return data;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return { devices: 0 };
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  const { data } = await api.post("/push/unsubscribe", { endpoint });
  return data;
}
